import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { TestNotFound } from '../../components/shared/TestNotFound';
import {
  Science,
  UploadFile,
  BarChart,
  PhotoCamera,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FieldEvidenceProvider } from '../../components/evidence/FieldEvidenceContext';
import { EvidenceNagLine } from '../../components/evidence/EvidenceNagLine';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { ROUTES } from '../../constants/routes';
import {
  getDirectShearData,
  saveDirectShearData,
  getLeebReadings,
  saveLeebReadings,
} from '../../services/directShearService';
import type {
  DirectShearGeneral,
  DirectShearStage,
  TestContext,
  TestNavigation,
} from '../../types/directShear';
import { DISPLACEMENT_RATE_DEFAULT } from './config/directShearFields';
import { TestFooterActions } from './components/TestFooterActions';
import { TestActionBar } from './components/TestActionBar';
import { useReportPreview } from '../../components/reportPreview/useReportPreview';
import { TestParametersTab } from './TestParametersTab';

import { PhotosTab } from './photos/PhotosTab';
import { ResultEntryRouter } from './result-entry/ResultEntryRouter';
import { AnalysisPlotsPage } from './analysis-plots/AnalysisPlotsPage';
import { downloadReport } from '../../services/reportService';
import { cacheTestCodeForWod } from '../../constants/splashArt';
import { TEST_CODES } from '../../constants/testCodes';
import { usePermissions } from '../../hooks/usePermissions';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { useRegisterUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { AtterbergWorkflowPage } from './atterberg/AtterbergWorkflowPage';
import { PSDWorkflowPage } from './psd/PSDWorkflowPage';
import { TriaxialWorkflowPage } from './triaxial/TriaxialWorkflowPage';
import { RockTCSWorkflowPage } from './rock-tcs/RockTCSWorkflowPage';
import { RockBTSWorkflowPage } from './rock-bts/RockBTSWorkflowPage';
import { RockUCSWorkflowPage } from './rock-ucs/RockUCSWorkflowPage';
import { RockUCSNSWorkflowPage } from './rock-ucsns/RockUCSNSWorkflowPage';
import { RockPLTWorkflowPage } from './point-load/RockPLTWorkflowPage';
import { RockCercharWorkflowPage } from './rock-cerchar/RockCercharWorkflowPage';

const EMPTY_GENERAL: DirectShearGeneral = {
  ellipseLengthA: null, ellipseLengthB: null, jrc: null, jointAngle: null,
  dip: null, dipDirection: null, leebHardnessFreshSurfaceTop: null, leebHardnessFreshSurfaceBottom: null,
  leebHardnessJointSurfaceTop: null, leebHardnessJointSurfaceBottom: null,
  displacementRateDescription: null, testType: null, directionOfShear: null, remarks: null,
  additionalObservations: null,
};

// BUG-2026-0201: seed the common displacement-rate statement as a REAL value
// whenever a test carries none, so the pre-fill the tech sees in the form is
// the same value that persists on Save and prints on the report. Applied to
// every server->state hydration below (load, silent refresh, post-save).
const withGeneralDefaults = (g: DirectShearGeneral): DirectShearGeneral =>
  (g.displacementRateDescription ?? '').trim() === ''
    ? { ...g, displacementRateDescription: DISPLACEMENT_RATE_DEFAULT }
    : g;

const EMPTY_STAGES: DirectShearStage[] = Array.from({ length: 5 }, (_, i) => ({
  stageNumber: i + 1, area: null, initialStress: null, initialLoad: null, verticalStressOffset: null,
}));

const TAB_DEFS = [
  { label: 'Test Parameters', icon: <Science sx={{ fontSize: 18 }} /> },
  { label: 'Result Entry', icon: <UploadFile sx={{ fontSize: 18 }} /> },
  { label: 'Analysis & Plots', icon: <BarChart sx={{ fontSize: 18 }} /> },
  { label: 'Photos', icon: <PhotoCamera sx={{ fontSize: 18 }} /> },
];

export const TestWorkflowPage = () => {
  const { projectId, workOrderId, workOrderDetailKey } = useParams<{
    projectId: string; workOrderId: string; workOrderDetailKey: string;
  }>();
  const navigate = useNavigate();
  const { openPreview, openHistory, previewDialog } = useReportPreview(workOrderDetailKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { canEditTestData, canSaveTestData } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTabState] = useState(() => {
    const t = parseInt(searchParams.get('tab') || '0', 10);
    return t >= 0 && t < TAB_DEFS.length ? t : 0;
  });

  const setActiveTab = useCallback((tab: number) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 0) next.delete('tab');
      else next.set('tab', String(tab));
      // Clear sub-tab when switching main tabs
      next.delete('stage');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [general, setGeneral] = useState<DirectShearGeneral>(EMPTY_GENERAL);
  const [stages, setStages] = useState<DirectShearStage[]>(EMPTY_STAGES);
  const [testStatus, setTestStatus] = useState('');
  const [context, setContext] = useState<TestContext>({
    locationName: '', sampleReference: '', depth: 0, testName: '', workOrderId: '',
    projectId: '', projectName: '', clientName: '', siteName: '', sampleType: '', siteKey: '',
  });
  const [navigation, setNavigation] = useState<TestNavigation>({
    previousWorkOrderDetailKey: null, nextWorkOrderDetailKey: null, currentIndex: 0, totalSamples: 0,
  });
  // Leeb Hardness Joint Surface 12-reading state
  const [leebTop, setLeebTop] = useState<(number | null)[]>(Array(12).fill(null));
  const [leebBottom, setLeebBottom] = useState<(number | null)[]>(Array(12).fill(null));
  const [leebDirty, setLeebDirty] = useState(false);

  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportLabel, setReportLabel] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Remember which test lives at this workOrderDetailKey so the splash screen
  // can show the test's own medallion on the NEXT full page load (sync read).
  useEffect(() => {
    if (workOrderDetailKey && context.testCode) cacheTestCodeForWod(workOrderDetailKey, context.testCode);
  }, [workOrderDetailKey, context.testCode]);

  // Silent refresh — updates general/stages without showing full-page spinner.
  // Used after dilation range recalculation so plots refresh but page doesn't blank.
  const silentRefresh = useCallback(async () => {
    if (!workOrderDetailKey) return;
    try {
      const data = await getDirectShearData(workOrderDetailKey);
      setGeneral(withGeneralDefaults(data.general ?? EMPTY_GENERAL));
      setStages(data.stages.length > 0 ? data.stages : EMPTY_STAGES);
    } catch { /* non-critical — next full load will catch it */ }
  }, [workOrderDetailKey]);

  const loadData = useCallback(async () => {
    if (!workOrderDetailKey) return;
    setLoading(true);
    try {
      const data = await getDirectShearData(workOrderDetailKey);
      setGeneral(withGeneralDefaults(data.general ?? EMPTY_GENERAL));
      setStages(data.stages.length > 0 ? data.stages : EMPTY_STAGES);
      setContext(data.context);
      setNavigation(data.navigation);
      setTestStatus((data.context as any)?.testStatus || '');
      setDirty(false);
      // Load leeb readings if general key exists
      const gk = (data.general as any)?.directShearGeneralKey || (data.general as any)?.DirectShearGeneralKey;
      if (gk) {
        try {
          const leeb = await getLeebReadings(gk);
          const toArr = (items: { readingNumber: number; value: number | null }[]) => {
            const arr: (number | null)[] = Array(12).fill(null);
            items.forEach((r) => { if (r.readingNumber >= 1 && r.readingNumber <= 12) arr[r.readingNumber - 1] = r.value; });
            return arr;
          };
          setLeebTop(toArr(leeb.top));
          setLeebBottom(toArr(leeb.bottom));
          setLeebDirty(false);
        } catch { /* no readings yet — keep defaults */ }
      }
    } catch (err) {
      // 404 = the test row doesn't exist (deleted, never created, or stale URL).
      // Render a clear "test not found" state instead of toasting a vague
      // "Failed to load test data" — that wording made deletions look like
      // infra outages and hid the real cause from non-engineering users.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotFound(true);
      } else {
        console.error('Failed to load test data:', err);
        setSnackbar({ open: true, message: 'Failed to load test data', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { loadData(); }, [loadData]);

  // BUG-2026-0189 — warn on browser refresh / close / back with unsaved edits.
  useUnsavedChangesWarning(dirty);

  const handleGeneralChange = (field: string, value: string | number | null) => {
    setGeneral((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleStageChange = (stageIndex: number, field: string, value: number | null) => {
    setStages((prev) => {
      const next = [...prev];
      next[stageIndex] = { ...next[stageIndex], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const handleLeebChange = (position: 'Top' | 'Bottom', readings: (number | null)[]) => {
    if (position === 'Top') setLeebTop(readings);
    else setLeebBottom(readings);
    setLeebDirty(true);
    setDirty(true);
  };

  const doSave = async (confirmReset = false) => {
    if (!workOrderDetailKey) return;
    setSaving(true);
    try {
      const result = await saveDirectShearData(workOrderDetailKey, general, stages, confirmReset);
      const gk = (general as any)?.directShearGeneralKey || (general as any)?.DirectShearGeneralKey;
      if (leebDirty || gk) {
        const data = await getDirectShearData(workOrderDetailKey);
        const freshGk = (data.general as any)?.directShearGeneralKey || (data.general as any)?.DirectShearGeneralKey;
        if (freshGk) {
          await saveLeebReadings(freshGk, 'Top', leebTop);
          await saveLeebReadings(freshGk, 'Bottom', leebBottom);
          setLeebDirty(false);
        }
        setGeneral(withGeneralDefaults(data.general ?? EMPTY_GENERAL));
        setStages(data.stages.length > 0 ? data.stages : EMPTY_STAGES);
        setTestStatus((data.context as any)?.testStatus || '');
      } else {
        await loadData();
      }
      setDirty(false);
      // lifecycleService peels one signoff level (e.g. Approved → Review
      // Completed), not a full reset. Keep the toast copy neutral — the
      // exact target status is in the grid already.
      const msg = result?.resetPerformed ? 'Saved — signoff reset one level' : 'Pre-test data saved';
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (err: any) {
      // Handle partial cascade reset required (409). The backend's
      // lifecycleService peels ONE signoff level per reset:
      //   Approved         → Review Completed  (clear ApprovedBy + ApprovalDate)
      //   Review Completed → In Progress       (clear ReviewedBy + ReviewDate)
      //   Test Completed   → In Progress       (clear CheckedBy  + TestDate)
      // The confirm copy mirrors the backend's targetStatus + fieldsToBeCleared
      // so the user sees exactly which level is about to drop.
      if (err?.response?.status === 409 && err?.response?.data?.error === 'TEST_REQUIRES_RESET') {
        const {
          currentStatus,
          targetStatus,
          fieldsToBeCleared = [],
        } = err.response.data as {
          currentStatus: string;
          targetStatus?: string | null;
          fieldsToBeCleared?: string[];
        };
        const fieldList = fieldsToBeCleared.length ? fieldsToBeCleared.join(', ') : 'signoff fields';
        const isApproved = currentStatus === 'Approved';
        setConfirmDlg({
          open: true,
          title: isApproved ? 'Warning: Test has been approved' : 'Warning: Test has been reviewed',
          message: targetStatus
            ? `This test is currently "${currentStatus}". Modifying test data will drop it back to "${targetStatus}" and clear: ${fieldList}. The test will need to be re-${isApproved ? 'approved' : 'reviewed'} afterwards.`
            : `This test is currently "${currentStatus}". Modifying test data will reset the signoff. Continue?`,
          confirmLabel: 'Yes, Reset and Save',
          confirmColor: 'warning',
          onConfirm: () => doSave(true),
        });
        setSaving(false);
        return;
      }
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => doSave(false);

  // BUG-2026-0189 — one boolean-returning silent save reused by Next/Prev sample,
  // auto-save-on-tab, and the unsaved-changes guard. Saves general+stages (+ Leeb
  // best-effort). Returns false on failure so the guard keeps the user on the page.
  const autoSaveIfDirty = async (): Promise<boolean> => {
    if (!dirty || !workOrderDetailKey) return true;
    setSaving(true);
    try {
      await saveDirectShearData(workOrderDetailKey, general, stages);
      if (leebDirty) {
        try {
          const data = await getDirectShearData(workOrderDetailKey);
          const gk = (data.general as any)?.directShearGeneralKey || (data.general as any)?.DirectShearGeneralKey;
          if (gk) {
            await saveLeebReadings(gk, 'Top', leebTop);
            await saveLeebReadings(gk, 'Bottom', leebBottom);
            setLeebDirty(false);
          }
        } catch { /* Leeb is best-effort; params already persisted */ }
      }
      setDirty(false);
      return true;
    } catch {
      setSnackbar({ open: true, message: 'Failed to auto-save', severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // BUG-2026-0189 — register the shared unsaved-changes guard + auto-save on tab switch.
  const requestLeave = useRegisterUnsavedGuard({ dirty, save: () => autoSaveIfDirty(), setConfirm: setConfirmDlg });
  const handleTabChange = (idx: number) => {
    void autoSaveIfDirty();
    setActiveTab(idx);
  };

  const handleNextSample = async () => {
    if (!navigation.nextWorkOrderDetailKey || !projectId || !workOrderId) return;
    if (!(await autoSaveIfDirty())) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', navigation.nextWorkOrderDetailKey));
  };

  const handlePreviousSample = async () => {
    if (!navigation.previousWorkOrderDetailKey || !projectId || !workOrderId) return;
    if (!(await autoSaveIfDirty())) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', navigation.previousWorkOrderDetailKey));
  };

  const handleCancel = () => {
    const goBack = () => {
      if (projectId && workOrderId) {
        navigate(ROUTES.WORK_ORDER_DETAIL
          .replace(':projectId', projectId)
          .replace(':workOrderId', workOrderId));
      }
    };
    // BUG-2026-0189 — shared Save & Leave / Discard & Leave / Cancel dialog.
    requestLeave(goBack);
  };

  const handleGenerateReport = async () => {
    if (!workOrderDetailKey) return;
    if (!general.directShearGeneralKey) {
      setSnackbar({ open: true, message: 'No test data available. Save test parameters first.', severity: 'error' });
      return;
    }
    setReportGenerating(true);
    setReportProgress(0);
    setReportLabel('Starting report generation...');
    const steps = [
      { pct: 10, label: 'Collecting test data...', delay: 300 },
      { pct: 30, label: 'Loading readings...', delay: 1000 },
      { pct: 50, label: 'Building report template...', delay: 2500 },
      { pct: 65, label: 'Launching renderer...', delay: 4000 },
      { pct: 80, label: 'Rendering charts...', delay: 6000 },
      { pct: 90, label: 'Generating PDF...', delay: 9000 },
    ];
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const step = [...steps].reverse().find((s) => elapsed >= s.delay);
      if (step) { setReportProgress(step.pct); setReportLabel(step.label); }
    }, 200);
    try {
      await downloadReport(workOrderDetailKey);
      setReportProgress(100);
      setReportLabel('Download complete');
      setSnackbar({ open: true, message: 'Report downloaded', severity: 'success' });
      setTimeout(() => { setReportProgress(0); setReportLabel(''); }, 2000);
    } catch (err) {
      const msg = (err as any)?.response?.data?.error || 'Report generation failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      clearInterval(timer);
      setReportGenerating(false);
    }
  };

  const breadcrumbs = [
    { label: context.clientName || '...', path: ROUTES.CLIENTS, entityType: 'client' as const },
    { label: context.projectName || context.projectId || '...', path: ROUTES.PROJECTS, entityType: 'project' as const },
    { label: context.siteName || '...',
      path: context.siteKey ? ROUTES.SITE_DETAIL.replace(':siteKey', context.siteKey) : undefined,
      entityType: 'site' as const },
    { label: context.locationName || '...',
      path: projectId ? ROUTES.LOCATIONS.replace(':projectId', projectId) : undefined,
      entityType: 'location' as const },
    { label: context.workOrderId || '...',
      path: projectId && workOrderId && !workOrderId.startsWith(':')
        ? ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId)
        : undefined,
      entityType: 'workOrder' as const },
    { label: context.sampleReference || '...', entityType: 'sample' as const },
    { label: context.testName || '...', entityType: 'test' as const },
  ];

  if (notFound) {
    return <TestNotFound entity="test" />;
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Delegation router — keys on the STABLE Test.TestCode (BUG-2026-0197), so a
  // test renamed in the admin Test Catalogue still routes to the right page.
  // `context.testName` is display-only from here down.

  // Delegate Atterberg to its own workflow page — different state shape (trials,
  // not stages), no Photos tab. Keeps Direct Shear logic in this file untouched.
  if (context.testCode === TEST_CODES.atterberg) {
    return <AtterbergWorkflowPage />;
  }

  // Delegate PSD (Particle Size Distribution / Grain Size Analysis — one code) —
  // sieve + hydrometer grids, gradation curve, no Photos.
  if (context.testCode === TEST_CODES.psd) {
    return <PSDWorkflowPage />;
  }

  // Delegate Triaxial (CU + CD) — GDS file upload, multi-phase readings,
  // 6 charts, Initial/Post-Cons/Final properties table, photo cropping.
  if (context.testCode === TEST_CODES.triaxialCu || context.testCode === TEST_CODES.triaxialCd) {
    return <TriaxialWorkflowPage />;
  }

  // Delegate Rock TCS (ASTM D7012 Method B) — conformity entry, GCTS CSV upload, 17×14 analysis.
  if (context.testCode === TEST_CODES.rockTcs) return <RockTCSWorkflowPage />;

  // Delegate Rock BTS (ASTM D3967) — 1/2-specimen entry, failure descriptions, photo page.
  if (context.testCode === TEST_CODES.rockBts) return <RockBTSWorkflowPage />;

  // BUG-2026-0140 name swap: plain 'Rock Uniaxial Compressive Strength' is the
  // NO-STRAIN test (code rockUcsNs, Method C); the strain/modulus test (Method D)
  // is 'Rock Uniaxial Compressive Strength with Modulus' (code rockUcs). Mig 094.
  // Delegate Rock UCSM (ASTM D7012 Method D) — confining-stress removed, all other TCS tabs kept.
  if (context.testCode === TEST_CODES.rockUcs) return <RockUCSWorkflowPage />;

  // Delegate Rock UCS (ASTM D7012 Method C) — single max-load per specimen, no strain page.
  if (context.testCode === TEST_CODES.rockUcsNs) return <RockUCSNSWorkflowPage />;

  // Delegate Point Load Test (ASTM D5731) — batch table of all PLT samples in
  // the work order (one editable table, one row per sample), table report.
  if (context.testCode === TEST_CODES.pointLoad) return <RockPLTWorkflowPage />;

  // Delegate Cerchar Abrasivity (ASTM D7625) — 5-stylus entry, CAI/classification, photo page.
  if (context.testCode === TEST_CODES.cerchar) return <RockCercharWorkflowPage />;

  // Guard: show "Under Construction" for any test that is NOT the Direct Shear
  // default handled below. Every implemented test type is delegated above; only
  // Direct Shear (code directShear) renders the in-file workflow. A NULL/unknown
  // code (e.g. Consolidation) has no module yet.
  if (context.testCode !== TEST_CODES.directShear) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ mb: 1 }}>
          <Breadcrumb items={breadcrumbs} />
        </Box>
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 2, py: 8,
        }}>
          <Science sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Under Moftaki Construction
          </Typography>
          <Typography variant="body1" color="text.disabled" sx={{ maxWidth: 400, textAlign: 'center' }}>
            The "{context.testName}" module is not yet available. We're working hard to bring it to you soon.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
            {navigation.previousWorkOrderDetailKey && (
              <Button variant="outlined" onClick={handlePreviousSample} sx={{ textTransform: 'none' }}>
                Previous Sample
              </Button>
            )}
            <Button variant="outlined" onClick={handleCancel} sx={{ textTransform: 'none' }}>
              Back to Work Order
            </Button>
            {navigation.nextWorkOrderDetailKey && (
              <Button variant="outlined" onClick={handleNextSample} sx={{ textTransform: 'none' }}>
                Next Sample
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  const page = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Breadcrumb items={breadcrumbs} />
            {context.depth != null && context.depth !== 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Depth: {context.depth} m
              </Typography>
            )}
            {testStatus && (
              <Chip
                label={testStatus}
                size="small"
                color={
                  testStatus === 'Approved' ? 'success'
                  : testStatus === 'Review Completed' ? 'info'
                  : testStatus === 'In Progress' ? 'warning'
                  : 'default'
                }
                variant="filled"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
            {/* Passive evidence nag — a marker, never a popup or save blocker */}
            <EvidenceNagLine />
          </Box>
        </Box>
        {previewDialog}
        <TestActionBar
          leadingStatus={reportGenerating && reportLabel ? (
            <Typography variant="body2" className="print-hide" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
              {reportLabel} ({reportProgress}%)
            </Typography>
          ) : undefined}
          saveHidden={false /* BUG-2026-0200: header Save restored on ALL Direct Shear tabs. Result Entry / Analysis also persist through their own in-tab flows (Upload / Process Stage / Calculate / Apply & Recalculate), but admins asked for the header Save — which saves Test Parameters (general + stage setup + Leeb) — to be available everywhere. canSave still gates by permission. */}
          canSave={canSaveTestData}
          onSave={handleSave}
          saving={saving}
          dirty={dirty}
          onPrint={() => {
            if (activeTab !== 0) setActiveTab(0);
            setTimeout(() => window.print(), 60);
          }}
          canPrint={!reportGenerating}
          onGenerateReport={handleGenerateReport}
          onPreviewReport={openPreview}
          onReportHistory={openHistory}
          reportGenerating={reportGenerating}
          pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
        />
      </Box>
      {reportGenerating && (
        <LinearProgress variant="determinate" value={reportProgress} className="print-hide" sx={{ height: 4, mb: 0.5, borderRadius: 2 }} />
      )}

      {/* Tab bar — full-width grid */}
      <Box className="print-hide" sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', mb: 1.5 }}>
        {TAB_DEFS.map((tab, idx) => {
          const isActive = activeTab === idx;
          const isDisabled = Boolean('disabled' in tab && (tab as Record<string, unknown>).disabled);
          return (
            <Box
              key={tab.label}
              onClick={() => !isDisabled && handleTabChange(idx)}
              sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                cursor: isDisabled ? 'default' : 'pointer',
                transition: 'background-color 0.2s ease',
                borderBottom: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                backgroundColor: isActive ? theme.palette.background.paper : '#F0F0EA',
                color: isDisabled
                  ? alpha(theme.palette.text.primary, 0.3)
                  : isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: isActive ? 700 : 400,
                '&:hover': !isDisabled ? {
                  backgroundColor: isActive
                    ? theme.palette.background.paper
                    : alpha(theme.palette.primary.main, 0.06),
                } : undefined,
                '& .MuiSvgIcon-root': {
                  color: isDisabled
                    ? alpha(theme.palette.text.primary, 0.25)
                    : isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  fontSize: 18,
                },
              }}
            >
              {tab.icon}
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 'inherit' }}>
                {tab.label}
              </Typography>
              {isDisabled && (
                <Typography variant="caption" sx={{
                  fontSize: '0.6rem', ml: 0.5,
                  color: alpha(theme.palette.text.primary, 0.25),
                }}>
                  Soon
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && (
          <TestParametersTab
            workOrderDetailKey={workOrderDetailKey}
            general={general}
            stages={stages}
            onGeneralChange={handleGeneralChange}
            onStageChange={handleStageChange}
            leebTop={leebTop}
            leebBottom={leebBottom}
            onLeebChange={handleLeebChange}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 1 && (
          <ResultEntryRouter
            testCode={context.testCode ?? null}
            directShearGeneralKey={general.directShearGeneralKey}
            savedDilationRange={(general as any).dilationRange ?? (general as any).DilationRange ?? null}
            stageDilationRanges={Object.fromEntries(stages.map((s) => [s.stageNumber, s.dilationRange ?? null]))}
            onCalculated={silentRefresh}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 2 && (
          <AnalysisPlotsPage generalKey={general.directShearGeneralKey || ''} stages={stages}
            savedDilationRange={(general as any).dilationRange ?? (general as any).DilationRange ?? null}
            onRecalculated={silentRefresh} />
        )}
        {activeTab === 3 && workOrderDetailKey && <PhotosTab workOrderDetailKey={workOrderDetailKey} />}
      </Box>

      {activeTab === 0 && (
        <Box className="print-hide">
          <TestFooterActions
            onSave={handleSave}
            onNextSample={handleNextSample}
            onPreviousSample={handlePreviousSample}
            onCancel={handleCancel}
            saving={saving}
            dirty={dirty}
            hasNext={!!navigation.nextWorkOrderDetailKey}
            hasPrevious={!!navigation.previousWorkOrderDetailKey}
            hideSave={!canSaveTestData}
            pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );

  // Direct Shear field-level evidence (JRC): provider feeds the camera button
  // in CommonParametersForm + the header nag pill.
  return workOrderDetailKey ? (
    <FieldEvidenceProvider workOrderDetailKey={workOrderDetailKey} testTypeId="directShear" readOnly={!canEditTestData}>
      {page}
    </FieldEvidenceProvider>
  ) : page;
};
